/* State engine for the ONE editing loop (BUILD-JOURNEY §1):
 * SEE real stored JSON → COPY → PASTE → VALIDATE → SAVE.
 * Validation is the engine's, not the UI's — this hook only gates on JSON
 * parse, then relays BeXO's validator errors[] verbatim. */
import {
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react';
import {
    notify
} from 'shared/api/notifier';
import {
    describeError,
    extractValidatorErrors,
    extractWarnings
} from '../api/appEngine.service';
import {
    useAppStudio
} from '../AppStudioContext';
import type {
    ValidateResult
} from '../api/types';

export interface JsonDocApi {
    text: string;
    setText: (t: string) => void;
    dirty: boolean;
    busy: boolean;
    parseError: string | null;
    parseErrorLine: number | null;
    errors: string[];
    /** Advisory warnings (locked contract) — non-blocking, ride along
     * on validate AND successful saves. */
    warnings: string[];
    /** Subtle note after a sanitized paste (system fields dropped). */
    pasteNote: string | null;
    lastSavedAt: Date | null;
    copyOut: () => Promise<void>;
    pasteIn: () => Promise<void>;
    format: () => void;
    /** Drop unsaved edits — reset to the last stored/saved baseline. */
    discard: () => void;
    runValidate: () => Promise<boolean>;
    runSave: () => Promise<boolean>;
}

export function stringifyDoc(doc: any): string {
    return JSON.stringify(doc, null, 2);
}

/** Line number (1-based) from a JSON.parse "at position N" message. */
function lineFromParseError(message: string, text: string): number | null {
    const m = /position (\d+)/.exec(message);
    if (!m) return null;
    const pos = Math.min(Number(m[1]), text.length);
    return text.slice(0, pos).split('\n').length;
}

export function useJsonDoc(opts: {
    docKey: string;
    initialText: string;
    save: (doc: any) => Promise<any>;
    validate?: (doc: any) => Promise<ValidateResult>;
    /** SYSTEM FIELDS: applied to PASTED JSON only — returns the display
     * doc + whether anything was dropped (drives pasteNote). */
    sanitizePaste?: (doc: any) => {
        doc: any;
        stripped: boolean;
    };
    onSaved?: (doc: any) => void;
}): JsonDocApi {
    const {
        setDirty
    } = useAppStudio();
    const [text, setTextRaw] = useState(opts.initialText);
    const [dirty, setDirtyLocal] = useState(false);
    const [busy, setBusy] = useState(false);
    const [parseError, setParseError] = useState<string | null>(null);
    const [parseErrorLine, setParseErrorLine] = useState<number | null>(null);
    const [errors, setErrors] = useState<string[]>([]);
    const [warnings, setWarnings] = useState<string[]>([]);
    const [pasteNote, setPasteNote] = useState<string | null>(null);
    const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
    const baselineRef = useRef(opts.initialText);
    const optsRef = useRef(opts);
    optsRef.current = opts;

    // New doc loaded (or saved baseline refresh) → reset editor state.
    useEffect(() => {
        baselineRef.current = opts.initialText;
        setTextRaw(opts.initialText);
        setDirtyLocal(false);
        setParseError(null);
        setParseErrorLine(null);
        setErrors([]);
        setWarnings([]);
        setPasteNote(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opts.docKey, opts.initialText]);

    // Mirror dirty into the shell registry + unregister on unmount.
    useEffect(() => {
        setDirty(opts.docKey, dirty);
        return () => setDirty(opts.docKey, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dirty, opts.docKey]);

    // Native tab-close guard while dirty.
    useEffect(() => {
        if (!dirty) return;
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [dirty]);

    const setText = useCallback((t: string) => {
        setTextRaw(t);
        setDirtyLocal(t !== baselineRef.current);
        setParseError(null);
        setParseErrorLine(null);
        setPasteNote(null);
    }, []);

    /** Local gate: returns the parsed doc or null (sets parse error UI). */
    const parse = useCallback((): any | null => {
        try {
            return JSON.parse(text);
        } catch (e: any) {
            const message = e?.message || 'Invalid JSON';
            setParseError(message);
            setParseErrorLine(lineFromParseError(message, text));
            setErrors([]);
            return null;
        }
    }, [text]);

    const copyOut = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(text);
            notify('Copied document to clipboard', 'success');
        } catch {
            notify('Clipboard unavailable — select the text and copy manually', 'warning');
        }
    }, [text]);

    const pasteIn = useCallback(async () => {
        try {
            const t = await navigator.clipboard.readText();
            if (!t || !t.trim()) {
                notify('Clipboard is empty', 'warning');
                return;
            }
            // Pasted system fields are silently dropped (display stays
            // the stripped projection; the save re-injects SERVER truth).
            const sanitize = optsRef.current.sanitizePaste;
            if (sanitize) {
                try {
                    const res = sanitize(JSON.parse(t));
                    if (res.stripped) {
                        setText(stringifyDoc(res.doc));
                        setPasteNote('System fields are managed automatically '
                            + '— pasted values were dropped.');
                        return;
                    }
                } catch {
                    // Not JSON — paste raw; the parse gate reports it.
                }
            }
            setText(t);
        } catch {
            notify('Clipboard blocked — paste directly into the editor (Ctrl+V)', 'warning');
        }
    }, [setText]);

    const format = useCallback(() => {
        const doc = parse();
        if (doc == null) return;
        setText(stringifyDoc(doc));
    }, [parse, setText]);

    /** The in-app way out of a dirty editor (no native confirm needed):
     * back to the last baseline — stored doc or latest successful save. */
    const discard = useCallback(() => {
        setTextRaw(baselineRef.current);
        setDirtyLocal(false);
        setParseError(null);
        setParseErrorLine(null);
        setErrors([]);
        setWarnings([]);
        setPasteNote(null);
    }, []);

    const runValidate = useCallback(async (): Promise<boolean> => {
        const doc = parse();
        if (doc == null) return false;
        const validate = optsRef.current.validate;
        if (!validate) {
            setErrors([]);
            notify('JSON is well-formed', 'success');
            return true;
        }
        setBusy(true);
        try {
            const res = await validate(doc);
            setErrors(res.errors || []);
            setWarnings(res.warnings || []);
            if (res.valid) {
                const n = (res.warnings || []).length;
                notify(n > 0
                    ? `Document is valid — ${n} advisor${n > 1 ? 'ies' : 'y'}`
                    : 'Document is valid', 'success');
            }
            return res.valid;
        } catch (err: any) {
            const validatorErrors = extractValidatorErrors(err);
            if (validatorErrors) setErrors(validatorErrors);
            else notify(describeError(err), 'danger');
            return false;
        } finally {
            setBusy(false);
        }
    }, [parse]);

    const runSave = useCallback(async (): Promise<boolean> => {
        const doc = parse();
        if (doc == null) return false;
        setBusy(true);
        try {
            const res = await optsRef.current.save(doc);
            // Saves SUCCEED with warnings (non-blocking second channel).
            const w = extractWarnings(res);
            const pretty = stringifyDoc(doc);
            baselineRef.current = pretty;
            setTextRaw(pretty);
            setDirtyLocal(false);
            setErrors([]);
            setWarnings(w);
            setLastSavedAt(new Date());
            notify(w.length > 0
                ? `Saved — ${w.length} advisor${w.length > 1 ? 'ies' : 'y'}`
                : 'Saved', 'success');
            optsRef.current.onSaved?.(doc);
            return true;
        } catch (err: any) {
            const validatorErrors = extractValidatorErrors(err);
            if (validatorErrors) setErrors(validatorErrors);
            else notify(describeError(err), 'danger');
            return false;
        } finally {
            setBusy(false);
        }
    }, [parse]);

    return {
        text,
        setText,
        dirty,
        busy,
        parseError,
        parseErrorLine,
        errors,
        warnings,
        pasteNote,
        lastSavedAt,
        copyOut,
        pasteIn,
        format,
        discard,
        runValidate,
        runSave
    };
}
